import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { ArrowLeft, Clock, FileText, BarChart3, Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useDecisionAnalysisStore } from '../stores/decisionAnalysisStore'
import { AnalysisHistoryDetail } from '../components/AnalysisHistoryDetail'
import { AnalysisHistoryService } from '../services/analysisHistoryService'

export const HistoryPage: React.FC = () => {
  const navigate = useNavigate()
  const { decisionHistory, loadHistoryFromDatabase, loadHistoryByType, deleteHistoryItem } = useDecisionAnalysisStore()
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null)
  const [filterType, setFilterType] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [stats, setStats] = useState<{ total: number; byType: Record<string, number> }>({ total: 0, byType: {} })

  // Load history data
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)
      try {
        await loadHistoryFromDatabase()
        const historyStats = await AnalysisHistoryService.getHistoryStats()
        setStats(historyStats)
      } catch (error) {
        console.error('Error loading history data:', error)
      } finally {
        setIsLoading(false)
      }
    }
    loadData()
  }, [loadHistoryFromDatabase])

  // Handle filter type changes
  useEffect(() => {
    if (filterType !== 'all') {
      loadHistoryByType(filterType)
    } else {
      loadHistoryFromDatabase()
    }
  }, [filterType, loadHistoryByType, loadHistoryFromDatabase])

  const getAnalysisType = (result: any) => {
    if (result && 'recommendedOption' in result) {
      return 'pros-cons'
    } else if (result && 'personalizedInsights' in result) {
      return 'context'
    } else if (result && 'options' in result) {
      return 'multi-option'
    } else if (result && 'nodes' in result) {
      return 'causal-chain'
    }
    return 'unknown'
  }

  const getAnalysisTypeInfo = (type: string) => {
    switch (type) {
      case 'pros-cons':
        return { name: 'Pros & Cons Analysis', icon: '⚖️', color: 'bg-blue-100 text-blue-800' }
      case 'context':
        return { name: 'Context Analysis', icon: '👤', color: 'bg-purple-100 text-purple-800' }
      case 'multi-option':
        return { name: 'Multi-option Analysis', icon: '📊', color: 'bg-green-100 text-green-800' }
      case 'causal-chain':
        return { name: 'Causal Chain Analysis', icon: '🔗', color: 'bg-orange-100 text-orange-800' }
      default:
        return { name: 'Unknown Analysis', icon: '❓', color: 'bg-gray-100 text-gray-800' }
    }
  }

  const filteredHistory = decisionHistory
    .filter(h => {
      // Type filtering
      if (filterType !== 'all' && getAnalysisType(h.result) !== filterType) {
        return false
      }
      // Search filtering
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        return (
          h.title.toLowerCase().includes(query) ||
          h.description.toLowerCase().includes(query) ||
          h.options.some((opt: any) => opt.text?.toLowerCase().includes(query))
        )
      }
      return true
    })

  const typeCounts = {
    'pros-cons': stats.byType['pros-cons'] || 0,
    'context': stats.byType['context'] || 0,
    'multi-option': stats.byType['multi-option'] || 0,
    'causal-chain': stats.byType['causal-chain'] || 0
  }

  return (
    <>
      <Helmet>
        <title>Analysis History - AI Decision Maker</title>
        <meta name="description" content="View all your analysis history" />
      </Helmet>

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back</span>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Analysis History</h1>
                <p className="text-gray-600">
                  View all your decision analysis history
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">{decisionHistory.length}</div>
              <div className="text-sm text-gray-500">Total Analyses</div>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="mb-6 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search analysis history..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Filter Tabs */}
          <div className="flex space-x-2 border-b border-gray-200">
            <button
              onClick={() => setFilterType('all')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                filterType === 'all'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              All ({decisionHistory.length})
            </button>
            <button
              onClick={() => setFilterType('pros-cons')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                filterType === 'pros-cons'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Pros & Cons ({typeCounts['pros-cons']})
            </button>
            <button
              onClick={() => setFilterType('context')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                filterType === 'context'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Context ({typeCounts['context']})
            </button>
            <button
              onClick={() => setFilterType('multi-option')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                filterType === 'multi-option'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Multi-option ({typeCounts['multi-option']})
            </button>
            <button
              onClick={() => setFilterType('causal-chain')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                filterType === 'causal-chain'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Causal Chain ({typeCounts['causal-chain']})
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex gap-6">
          {/* History List */}
          <div className="w-1/2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  {filterType === 'all' ? 'All Analyses' : getAnalysisTypeInfo(filterType).name}
                </h3>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {isLoading ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">Loading history...</p>
                  </div>
                ) : filteredHistory.length === 0 ? (
                  <div className="p-8 text-center">
                    <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No history yet</h3>
                    <p className="text-gray-500">Start your first analysis to see it here</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {filteredHistory.map((decision) => {
                      const analysisType = getAnalysisType(decision.result)
                      const typeInfo = getAnalysisTypeInfo(analysisType)
                      return (
                        <div
                          key={decision.id}
                          className={`p-4 cursor-pointer transition-colors ${
                            selectedHistoryId === decision.id
                              ? 'bg-blue-50 border-l-4 border-blue-500'
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => setSelectedHistoryId(decision.id)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="text-lg">{typeInfo.icon}</span>
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${typeInfo.color}`}>
                                  {typeInfo.name}
                                </span>
                              </div>
                              <h4 className="font-medium text-gray-900 mb-1">{decision.title}</h4>
                              <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                                {decision.description}
                              </p>
                              <div className="flex items-center space-x-4 text-xs text-gray-500">
                                <span>{decision.options.length} options</span>
                                <span>{decision.criteria.length} criteria</span>
                                <span>{new Date(decision.createdAt).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Details Panel */}
          <div className="w-1/2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Analysis Details</h3>
              </div>
              <div className="p-4">
                {selectedHistoryId ? (
                  (() => {
                    const history = decisionHistory.find(h => h.id === selectedHistoryId)
                    if (!history) {
                      return (
                        <div className="text-center py-8">
                          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-500">No details found</p>
                        </div>
                      )
                    }

                    return (
                      <AnalysisHistoryDetail 
                        history={history}
                        onDelete={deleteHistoryItem}
                      />
                    )
                  })()
                ) : (
                  <div className="text-center py-8">
                    <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Select an analysis</h3>
                    <p className="text-gray-500">Choose an analysis from the list to view its details</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
} 